import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    FileText,
    Folder,
    MessageSquare,
    Plus,
} from 'lucide-react';
import { staffClient } from 'clients/staff';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import Loading from 'ui/Loading';
import SiteSearchBar from 'ui/SiteSearchBar';
import DocumentLibrary from 'ui/DocumentLibrary';
import ImageViewer from 'ui/ImageViewer';
import Tabs, { Tab } from 'ui/Tabs';
import Button from 'ui/Button';
import Section from 'ui/Section';
import TableContainer from 'ui/TableContainer';
import FilterSection from 'ui/FilterSection';
import SearchInput from 'ui/SearchInput';
import Table, { Column } from 'ui/Table';
import { Badge } from 'ui/shadcn';
import { SiteCategory, SITE_CATEGORY_BADGE, SITE_CATEGORY_LABEL } from 'apis/site';
import { cn } from 'lib/utils';

type BadgeVariant = 'default' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';

// 현장 상태 (site.type) — 현장 목록과 같은 Badge 매핑
const SITE_TYPE: Record<string, { label: string; variant: BadgeVariant }> = {
    CONTRACT_COMPLETED: { label: '계약완료', variant: 'warning' },
    IN_PROGRESS: { label: '진행중', variant: 'success' },
    COMPLETED: { label: '준공', variant: 'secondary' },
    ETC: { label: '기타', variant: 'secondary' },
};

// 공종 — 현장 목록과 같은 Badge 매핑
const WORK_TYPE: Record<string, { label: string; variant: BadgeVariant }> = {
    ELECTRIC: { label: '전기', variant: 'warning' },
    FIRE: { label: '소방', variant: 'danger' },
    TELECOM: { label: '통신', variant: 'info' },
    NETWORK: { label: '통신', variant: 'info' },
    OTHER: { label: '기타', variant: 'secondary' },
};

// 계약 상태는 관리자 화면에서 자유 입력(예: '계약', '수행중'). 과거 코드값도 라벨로 변환.
const CONTRACT_STATUS_LABEL: Record<string, string> = {
    CONTRACT: '계약',
    PERFORMING: '수행중',
    COMPLETED: '준공',
    OTHER: '기타',
};
const getContractStatusVariant = (label: string): BadgeVariant => {
    if (label.includes('수행') || label.includes('진행')) return 'success';
    if (label.includes('계약')) return 'warning';
    return 'secondary';
};

const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

const WorkTypeBadges: React.FC<{ types?: string[] | null }> = ({ types }) => {
    if (!types || types.length === 0) return <span className="text-neutral-400">-</span>;
    return (
        <span className="inline-flex flex-wrap gap-1">
            {types.map((type) => (
                <Badge key={type} variant={WORK_TYPE[type]?.variant || 'secondary'}>
                    {WORK_TYPE[type]?.label || type}
                </Badge>
            ))}
        </span>
    );
};

/**
 * 감리 시작/종료일의 출처 — 현장정보에 직접 입력한 값이 있으면 MANUAL, 없으면 감리원 배치 계산값.
 * 구분·출처는 상태가 아니므로 시맨틱 색을 쓰지 않는다 (DESIGN.md §2-2).
 */
const SupervisionSourceBadge: React.FC<{ source?: string }> = ({ source }) =>
    source === 'MANUAL' ? (
        <Badge variant="default">현장정보 입력</Badge>
    ) : (
        <Badge variant="secondary">배치 계산</Badge>
    );

const SiteDetail = () => {
    const { siteId } = useParams();
    const navigate = useNavigate();
    const [siteData, setSiteData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const tabs: Tab[] = [
        {
            id: 'overview',
            label: '현장정보',
            icon: <FileText strokeWidth={1.5} />
        },
        {
            id: 'documents',
            label: '자료실',
            icon: <Folder strokeWidth={1.5} />
        },
        {
            id: 'board',
            label: '게시판',
            icon: <MessageSquare strokeWidth={1.5} />
        }
        // {
        //     id: 'schedule',
        //     label: '일정',
        //     icon: <CalendarDays strokeWidth={1.5} />
        // }
    ];

    useEffect(() => {
        fetchSiteData();
        // 현장 방문 기록 저장
        if (siteId) {
            localStorage.setItem('lastVisitedSiteId', siteId);
        }
    }, [siteId]);

    const fetchSiteData = async () => {
        try {
            const response = await staffClient.get(`/site/${siteId}`);
            setSiteData(response.data);
        } catch (error) {
            console.error('Failed to fetch site data:', error);
            // 더미 데이터
            setSiteData({
                name: '산성구역 주택재개발정비사업',
                location: '서울시 강남구 삼성동',
                type: 'IN_PROGRESS',
                startDate: '2024.01.01',
                endDate: '2027.12.31',
                progress: 45
            });
        } finally {
            setLoading(false);
        }
    };

    const renderTabContent = (tabId: string) => {
        switch (tabId) {
            case 'overview':
                return <OverviewTab siteData={siteData} />;
            case 'documents':
                return <DocumentsTab siteId={siteId} />;
            case 'board':
                return <BoardTab siteId={siteId} />;
            // case 'schedule':
            //     return <ScheduleTab siteId={siteId} />;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <Loading text="현장 정보를 불러오는 중..." />
            </PageContainer>
        );
    }

    const siteType = SITE_TYPE[siteData?.type || ''];

    // 사이드바 메뉴(감리현장/공사현장/설계현장)와 같은 이름으로 돌아가게 한다
    const siteCategory = (siteData?.category || 'SUPERVISION') as SiteCategory;
    const listLabel = `${SITE_CATEGORY_LABEL[siteCategory] || '현장'}현장`;
    const listPath = `/sites/list?category=${siteCategory}`;

    return (
        <PageContainer>
            <PageHeader
                title={siteData?.name || '현장명'}
                subtitle={siteData?.location || '주소 정보 없음'}
                badge={siteType ? <Badge variant={siteType.variant}>{siteType.label}</Badge> : undefined}
                breadcrumb={[
                    { label: listLabel, path: listPath, onClick: () => navigate(listPath) },
                    { label: siteData?.name || '현장 상세' }
                ]}
                actions={
                    <SiteSearchBar currentSiteId={siteId} />
                }
            />

            {/* 탭 네비게이션 및 콘텐츠 */}
            <Tabs
                tabs={tabs}
                defaultTab="overview"
                renderContent={renderTabContent}
                variant="default"
            />
        </PageContainer>
    );
};

// 정보 행 — key:value (카드 안에서 헤어라인으로 구분)
const Field: React.FC<{ label: string; value: string | React.ReactNode }> = ({ label, value }) => {
    return (
        <div className="flex items-center gap-4 border-b border-border py-2.5 last:border-b-0">
            <dt className="w-32 shrink-0 text-sm text-muted-foreground">{label}</dt>
            <dd className="min-w-0 flex-1 text-base text-foreground">{value}</dd>
        </div>
    );
};

// 현장개요 탭
const OverviewTab: React.FC<{ siteData: any }> = ({ siteData }) => {
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerInitialIndex, setViewerInitialIndex] = useState(0);
    const [supervisors, setSupervisors] = useState<any[]>([]);
    const [loadingSupervisors, setLoadingSupervisors] = useState(true);
    const [contracts, setContracts] = useState<any[]>([]);
    const [loadingContracts, setLoadingContracts] = useState(true);

    useEffect(() => {
        if (siteData?.id) {
            fetchSupervisors();
            fetchContracts();
        }
    }, [siteData]);

    const fetchSupervisors = async () => {
        try {
            const response = await staffClient.get(`/supervisor?siteId=${siteData.id}`);
            const supervisorData = Array.isArray(response.data) ? response.data : [];
            setSupervisors(supervisorData);
        } catch (error) {
            console.error('Failed to fetch supervisors:', error);
            setSupervisors([]);
        } finally {
            setLoadingSupervisors(false);
        }
    };

    const fetchContracts = async () => {
        try {
            const response = await staffClient.get(`/contract?siteId=${siteData.id}`);
            const contractData = Array.isArray(response.data) ? response.data : [];
            setContracts(contractData);
        } catch (error) {
            console.error('Failed to fetch contracts:', error);
            setContracts([]);
        } finally {
            setLoadingContracts(false);
        }
    };

    // 감리원을 타입별로 분류
    const chiefSupervisors = supervisors.filter(s => s.type === 'CHIEF');
    const assistantSupervisors = supervisors.filter(s => s.type === 'ASSISTANT');

    const progressText = siteData?.progressRate
        ? (siteData.progressRate.toString().includes('%') ? siteData.progressRate : `${siteData.progressRate}%`)
        : '0%';

    const renderSupervisorRow = (label: string, list: any[]) => (
        <Field
            label={label}
            value={
                list.length > 0 ? (
                    <div className="flex flex-wrap gap-x-5 gap-y-1">
                        {list.map((s) => (
                            <span key={s.id} className="inline-flex items-baseline gap-2">
                                <span className="font-medium text-foreground">{s.name}</span>
                                <span className="text-xs text-muted-foreground tabular-nums">
                                    {formatDate(s.startDate)} ~ {formatDate(s.endDate)}
                                </span>
                            </span>
                        ))}
                    </div>
                ) : (
                    <span className="text-neutral-400">-</span>
                )
            }
        />
    );

    const contractColumns: Column<any>[] = [
        {
            key: 'contractRevision',
            header: '구분',
            width: '70px',
            align: 'center',
            render: (value) => <span className="font-medium tabular-nums">{value}차</span>,
        },
        {
            key: 'contractDate',
            header: '계약일',
            width: '110px',
            align: 'center',
            render: (value) => <span className="tabular-nums">{formatDate(value)}</span>,
        },
        {
            key: 'status',
            header: '상태',
            width: '90px',
            align: 'center',
            render: (value) => {
                const raw = typeof value === 'string' ? value.trim() : '';
                if (!raw) return <span className="text-neutral-400">-</span>;
                const label = CONTRACT_STATUS_LABEL[raw] || raw;
                return <Badge variant={getContractStatusVariant(label)}>{label}</Badge>;
            },
        },
        {
            key: 'workTypes',
            header: '공종',
            width: '120px',
            render: (value) => <WorkTypeBadges types={value} />,
        },
        {
            key: 'businessApprovalDate',
            header: '사업시행인가일',
            width: '120px',
            align: 'center',
            render: (value) => <span className="text-muted-foreground tabular-nums">{formatDate(value)}</span>,
        },
        {
            key: 'biddingNoticeDate',
            header: '입찰공고일',
            width: '110px',
            align: 'center',
            render: (value) => <span className="text-muted-foreground tabular-nums">{formatDate(value)}</span>,
        },
        {
            key: 'remarks',
            header: '비고',
            render: (value) => value || <span className="text-neutral-400">-</span>,
        },
    ];

    return (
        <div className="space-y-4">
            {/* ── 현장 이미지 ── */}
            {siteData?.siteImgs && siteData.siteImgs.length > 0 && (
                <Section
                    title="현장 이미지"
                    description={`${siteData.siteImgs.length}장`}
                    actions={
                        <Button
                            size="small"
                            variant="ghost"
                            onClick={() => { setViewerInitialIndex(0); setViewerOpen(true); }}
                        >
                            전체 보기
                            <ChevronRight />
                        </Button>
                    }
                >
                    <div className="grid grid-cols-3 gap-2 md:grid-cols-4 lg:grid-cols-6">
                        {siteData.siteImgs.slice(0, 6).map((img: any, index: number) => (
                            <button
                                type="button"
                                key={index}
                                className="relative aspect-square cursor-pointer overflow-hidden rounded border border-border bg-neutral-100 transition-colors duration-fast hover:border-navy-700"
                                onClick={() => { setViewerInitialIndex(index); setViewerOpen(true); }}
                            >
                                <img
                                    src={img.url || img}
                                    alt={`${siteData.name} ${index + 1}`}
                                    className="h-full w-full object-cover"
                                />
                                {index === 0 && (
                                    <span className="absolute left-1.5 top-1.5 rounded bg-primary px-1.5 py-0.5 text-[11px] font-medium text-primary-foreground">대표</span>
                                )}
                                {index === 5 && siteData.siteImgs.length > 6 && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-neutral-900/60">
                                        <span className="text-md font-semibold text-white">+{siteData.siteImgs.length - 6}</span>
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                </Section>
            )}

            {/* ── 현장 정보 ── */}
            <Section title="현장 정보" bodyClassName="py-1.5">
                <div className="grid grid-cols-1 gap-x-10 xl:grid-cols-2">
                    <dl>
                        <Field
                            label="구분"
                            value={(() => {
                                const badge = SITE_CATEGORY_BADGE[(siteData?.category || 'SUPERVISION') as SiteCategory];
                                return badge ? <Badge variant={badge.variant}>{badge.label}</Badge> : (siteData?.category || '-');
                            })()}
                        />
                        <Field label="위치" value={siteData?.location || '-'} />
                        <Field
                            label="상태"
                            value={
                                SITE_TYPE[siteData?.type || ''] ? (
                                    <Badge variant={SITE_TYPE[siteData.type].variant}>{SITE_TYPE[siteData.type].label}</Badge>
                                ) : (siteData?.type || '-')
                            }
                        />
                        <Field label="시공사" value={siteData?.contractorName || '-'} />
                        <Field label="세대수" value={siteData?.numberOfUnits || '-'} />
                        <Field label="연면적" value={siteData?.totalFloorArea || '-'} />
                        <Field label="건축규모" value={siteData?.buildingScale || '-'} />
                    </dl>
                    <dl className="border-t border-border xl:border-t-0">
                        <Field label="공사 착공일" value={<span className="tabular-nums">{formatDate(siteData?.constructionStartDate)}</span>} />
                        <Field label="공사 준공일" value={<span className="tabular-nums">{formatDate(siteData?.constructionEndDate)}</span>} />
                        <Field label="감리 시작신고일" value={<span className="tabular-nums">{formatDate(siteData?.supervisionReportDate)}</span>} />
                        <Field label="감리원 투입일" value={<span className="tabular-nums">{formatDate(siteData?.supervisionDeployDate)}</span>} />
                        <Field label="감리 종료일" value={<span className="tabular-nums">{formatDate(siteData?.supervisionFinishDate)}</span>} />
                        <Field
                            label="감리 시작일 (적용)"
                            value={
                                <span className="inline-flex items-center gap-2">
                                    <span className="tabular-nums">{formatDate(siteData?.supervisionStartDate)}</span>
                                    <SupervisionSourceBadge source={siteData?.supervisionDateSource} />
                                </span>
                            }
                        />
                        <Field
                            label={
                                siteData?.type === 'COMPLETED'
                                    ? '감리 종료일 (적용 · 신고 완료)'
                                    : '감리 종료일 (적용 · 예정)'
                            }
                            value={
                                <span className="inline-flex items-center gap-2">
                                    <span className="tabular-nums">{formatDate(siteData?.supervisionEndDate)}</span>
                                    <SupervisionSourceBadge source={siteData?.supervisionDateSource} />
                                </span>
                            }
                        />
                        <Field label="공종" value={<WorkTypeBadges types={siteData?.workTypes} />} />
                        <Field
                            label="진행률"
                            value={
                                <div className="flex w-full items-center gap-2">
                                    <div className="h-1.5 max-w-[160px] flex-1 overflow-hidden rounded-full bg-neutral-100">
                                        <div
                                            className="h-full rounded-full bg-primary transition-all duration-300"
                                            style={{ width: progressText }}
                                        />
                                    </div>
                                    <span className="text-sm text-neutral-700 tabular-nums">{progressText}</span>
                                </div>
                            }
                        />
                    </dl>
                </div>
            </Section>

            {/* ── 감리원 ── */}
            <Section title="감리원" bodyClassName={loadingSupervisors || supervisors.length === 0 ? '' : 'py-1.5'}>
                {loadingSupervisors ? (
                    <Loading size="small" text="감리원 정보를 불러오는 중..." />
                ) : supervisors.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">등록된 감리원이 없습니다.</p>
                ) : (
                    <dl>
                        {renderSupervisorRow('책임감리', chiefSupervisors)}
                        {renderSupervisorRow('보조감리', assistantSupervisors)}
                    </dl>
                )}
            </Section>

            {/* ── 계약 정보 ── */}
            <TableContainer title="계약 정보" resultCount={loadingContracts ? undefined : contracts.length}>
                <Table
                    columns={contractColumns}
                    data={contracts}
                    loading={loadingContracts}
                    emptyMessage="등록된 계약 정보가 없습니다."
                />
            </TableContainer>

            {/* Image Viewer Modal */}
            {siteData?.siteImgs && (
                <ImageViewer
                    images={siteData.siteImgs.map((img: any) => img.url || img)}
                    initialIndex={viewerInitialIndex}
                    isOpen={viewerOpen}
                    onClose={() => setViewerOpen(false)}
                    titles={siteData.siteImgs.map((_: any, index: number) => `${siteData.name} ${index + 1}`)}
                />
            )}
        </div>
    );
};

// 자료실 탭
const DocumentsTab: React.FC<{ siteId: string | undefined }> = ({ siteId }) => {
    return <DocumentLibrary siteId={siteId} />;
};

// 게시판 탭
const BoardTab: React.FC<{ siteId: string | undefined }> = ({ siteId }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [board, setBoard] = useState<any>(null);
    const [articles, setArticles] = useState<any[]>([]);
    const [articlesLoading, setArticlesLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('전체');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const postsPerPage = 10;

    useEffect(() => {
        if (siteId) {
            fetchSiteBoard();
        }
    }, [siteId]);

    useEffect(() => {
        if (board) {
            fetchArticles();
        }
    }, [board, currentPage, categoryFilter, searchTerm]);

    const fetchSiteBoard = async () => {
        try {
            // 현장의 게시판 정보 가져오기
            const siteResponse = await staffClient.get(`/site/${siteId}`);
            const siteData = siteResponse?.data;

            if (siteData?.boards) {
                // board_type이 'general'인 게시판 찾기 (현장게시판)
                const siteBoard = siteData.boards.find((b: any) => b.board_type === 'general');
                if (siteBoard) {
                    setBoard(siteBoard);
                }
            }
        } catch (error) {
            console.error('Failed to fetch site board:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchArticles = async () => {
        if (!board) return;

        try {
            setArticlesLoading(true);
            const params: any = {
                boardId: board.id,
                offset: (currentPage - 1) * postsPerPage,
                limit: postsPerPage,
            };

            if (categoryFilter !== '전체') {
                const category = board.categories?.find((cat: any) => cat.name === categoryFilter);
                if (category) {
                    params.categoryId = category.id;
                }
            }

            if (searchTerm) {
                params.searchText = searchTerm;
            }

            const ret = await staffClient.get('/article', { params });
            const articlesData = ret?.data?.data || [];
            const meta = ret?.data?.meta || {};

            setArticles(articlesData);
            setTotalCount(meta.total || 0);
            setTotalPages(Math.ceil((meta.total || 0) / postsPerPage));
        } catch (error) {
            console.error('Failed to fetch articles:', error);
            setArticles([]);
        } finally {
            setArticlesLoading(false);
        }
    };

    const handleSearchChange = (value: string) => {
        setCurrentPage(1);
        setSearchTerm(value);
    };

    const handleCategoryChange = (name: string) => {
        setCurrentPage(1);
        setCategoryFilter(name);
    };

    const handleArticleClick = (articleId: number) => {
        navigate(`/boards/${board.id}/articles/${articleId}?from=site&siteId=${siteId}`);
    };

    const handleWriteClick = () => {
        navigate(`/boards/${board.id}/articles/editor?from=site&siteId=${siteId}`);
    };

    if (loading) {
        return <Loading text="게시판을 불러오는 중..." />;
    }

    if (!board) {
        return (
            <Section>
                <p className="py-8 text-center text-sm text-muted-foreground">현장 게시판이 없습니다.</p>
            </Section>
        );
    }

    const columns: Column<any>[] = [
        {
            key: 'id',
            header: '번호',
            width: '70px',
            align: 'center',
            render: (_value, _row, index) => (
                <span className="text-sm text-muted-foreground tabular-nums">
                    {(currentPage - 1) * postsPerPage + index + 1}
                </span>
            ),
        },
        {
            key: 'title',
            header: '제목',
            render: (value) => <span className="text-foreground">{value}</span>,
        },
        {
            key: 'writer',
            header: '작성자',
            width: '120px',
            align: 'center',
            render: (_value, row) =>
                row.writer?.name || <span className="text-neutral-400">익명</span>,
        },
        {
            key: 'category',
            header: '카테고리',
            width: '120px',
            render: (_value, row) =>
                row.category ? (
                    <Badge variant="secondary">{row.category.name}</Badge>
                ) : (
                    <span className="text-neutral-400">-</span>
                ),
        },
        {
            key: 'is_public',
            header: '공개',
            width: '80px',
            align: 'center',
            render: (_value, row) =>
                row.is_public !== false ? (
                    <Badge variant="success">공개</Badge>
                ) : (
                    <Badge variant="secondary">비공개</Badge>
                ),
        },
        {
            key: 'created_at',
            header: '작성일',
            width: '120px',
            align: 'center',
            render: (value) => (
                <span className="text-muted-foreground tabular-nums">{formatDate(value)}</span>
            ),
        },
    ];

    return (
        <div>
            <FilterSection>
                <SearchInput
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="제목으로 검색..."
                />

                {board.categories && board.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                        <CategoryChip
                            active={categoryFilter === '전체'}
                            onClick={() => handleCategoryChange('전체')}
                        >
                            전체
                        </CategoryChip>
                        {board.categories.map((category: any) => (
                            <CategoryChip
                                key={category.id}
                                active={categoryFilter === category.name}
                                onClick={() => handleCategoryChange(category.name)}
                            >
                                {category.name}
                            </CategoryChip>
                        ))}
                    </div>
                )}

                <Button icon={<Plus />} onClick={handleWriteClick}>
                    글쓰기
                </Button>
            </FilterSection>

            <TableContainer title="현장 게시판" resultCount={totalCount}>
                <Table
                    columns={columns}
                    data={articles}
                    loading={articlesLoading && articles.length === 0}
                    emptyMessage="게시글이 없습니다."
                    onRowClick={(row) => handleArticleClick(row.id)}
                />
                <ServerPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={postsPerPage}
                    totalCount={totalCount}
                    onPageChange={setCurrentPage}
                />
            </TableContainer>
        </div>
    );
};

// 서버 페이지네이션 — ui/Table 내장 페이저와 같은 모양
const ServerPagination: React.FC<{
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, pageSize, totalCount, onPageChange }) => {
    if (totalPages <= 1) return null;

    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    const pages: number[] = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    const startIdx = (currentPage - 1) * pageSize + 1;
    const endIdx = Math.min(currentPage * pageSize, totalCount);

    return (
        <div className="flex items-center justify-between gap-3 border-t border-border bg-neutral-50 px-4 py-3">
            <div className="text-xs text-muted-foreground tabular-nums">
                <span className="font-medium text-foreground">
                    {startIdx}–{endIdx}
                </span>{' '}
                / 전체 <span className="font-medium text-foreground">{totalCount}</span>건
            </div>
            <div className="flex items-center gap-1">
                <PageButton disabled={currentPage === 1} onClick={() => onPageChange(1)} aria-label="처음">
                    <ChevronsLeft className="h-4 w-4" />
                </PageButton>
                <PageButton disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} aria-label="이전">
                    <ChevronLeft className="h-4 w-4" />
                </PageButton>
                {pages.map((page) => (
                    <PageButton key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
                        <span className="tabular-nums">{page}</span>
                    </PageButton>
                ))}
                <PageButton disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)} aria-label="다음">
                    <ChevronRight className="h-4 w-4" />
                </PageButton>
                <PageButton disabled={currentPage === totalPages} onClick={() => onPageChange(totalPages)} aria-label="마지막">
                    <ChevronsRight className="h-4 w-4" />
                </PageButton>
            </div>
        </div>
    );
};

const PageButton: React.FC<
    React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }
> = ({ active, className, children, ...props }) => (
    <button
        type="button"
        className={cn(
            'inline-flex h-7 min-w-[28px] items-center justify-center rounded border border-transparent px-1.5 text-xs transition-colors duration-fast',
            active
                ? 'bg-primary text-primary-foreground'
                : 'text-neutral-700 hover:bg-muted disabled:text-neutral-400 disabled:hover:bg-transparent',
            'disabled:cursor-not-allowed',
            className
        )}
        {...props}
    >
        {children}
    </button>
);

const CategoryChip: React.FC<{
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}> = ({ active, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={cn(
            'inline-flex h-8 items-center rounded px-3 text-sm font-medium transition-colors duration-fast',
            active
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-neutral-700 hover:bg-accent hover:text-primary'
        )}
    >
        {children}
    </button>
);

// 일정 탭 (현재 비활성 — 탭 목록에서 주석 처리됨)
const ScheduleTab: React.FC<{ siteId: string | undefined }> = ({ siteId }) => {
    const schedules = [
        { day: '15', month: 'DEC', title: '월간 공정회의', time: '오전 10:00 - 12:00', location: '현장 사무실' },
        { day: '20', month: 'DEC', title: '안전 점검', time: '오후 2:00 - 4:00', location: '전 현장' },
    ];
    return (
        <Section
            title="현장 일정"
            flush
            actions={
                <Button size="small" variant="outline" icon={<Plus />}>
                    일정 추가
                </Button>
            }
        >
            <ul>
                {schedules.map((item) => (
                    <li key={item.title} className="flex items-center gap-4 border-b border-border px-4 py-3 last:border-b-0">
                        <div className="flex w-12 shrink-0 flex-col items-center rounded border border-border py-1">
                            <span className="text-lg font-semibold leading-tight text-foreground tabular-nums">{item.day}</span>
                            <span className="text-[11px] text-muted-foreground">{item.month}</span>
                        </div>
                        <div className="min-w-0">
                            <p className="text-md font-medium text-foreground">{item.title}</p>
                            <p className="text-sm text-muted-foreground">{item.time} · {item.location}</p>
                        </div>
                    </li>
                ))}
            </ul>
        </Section>
    );
};

export default SiteDetail;
